/*************************************************************************
 * @license
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* global document:readonly */
/* global window:readonly */
/* global App:readonly */

/**
 * launcher code for common entry points
 *
 * It will look for a <script src=".../entai-launcher.js"> in the HTML
 * HEAD or BODY (top level only), and load entai module JS & CSS (by default).
 *
 */

import { getPinnedVersionUri } from '@adobesign/as-dev-tools';

import loader from '../lib/loader';
import { makeReady } from './env';

const moduleName = 'entai';

const launcherName = 'entai-launcher\\.js';
const scriptLocator = `script[src*=\\/${launcherName}]`;

// Look for script in head first, then body.
let launcherScripts = document.querySelectorAll(`head > ${scriptLocator}`);
if (!launcherScripts.length) {
    launcherScripts = document.querySelectorAll(`body > ${scriptLocator}`);
}

if (!launcherScripts.length) {
    console.error('[entai-launcher] No launcher script found!');
    throw new Error('entai-launcher.js script not found in head>* or body>*');
}

// Get last matching script
const launcherScript = launcherScripts[launcherScripts.length - 1];
const srcUrl = new URL(launcherScript.src);

// clear hash and search
srcUrl.hash = '';
srcUrl.search = '';

// remove launcher script name
let baseUrl = srcUrl.href.replace(new RegExp(launcherName), '');

// ensure ending /
if (baseUrl && !/\/$/.test(baseUrl)) baseUrl += '/';

// Plugin domain is the launcher's folder, e.g. https://static.adobesigncdn.com/entai-cdn.
const pluginDomain = baseUrl.replace(/\/$/, '');

// Create a ready() promise for module
// NOTE: if module is loaded, the ready is replaced by another in env.js
App.Env.plugins[moduleName] = App.Env.plugins[moduleName] || {};
App.Env.plugins[moduleName].ready = makeReady();

function loadModule(versionUri) {
    App.Env.plugins[moduleName].assetBaseUrl = pluginDomain;
    const versionBase = versionUri.endsWith('/') ? versionUri.slice(0, -1) : versionUri;
    const module = `${versionBase}/${moduleName}`;

    // NOTE: warning only on CSS load error
    loader.css(`${module}.css`, { insertBefore: true })
        .catch(url => {
            console.warn(`[entai-launcher] Could not load CSS: ${url}`);
        });

    // JS load error rejects the Promise
    loader.js(`${module}.js`).catch(url => {
        App.Env.plugins[moduleName].ready.reject(new Error(`Could not load ${url}`));
    });
}

// Parse ?comp!entai-cdn=<version> here because as-dev-tools derives the prefix
// from the launcher path, which is not entai-cdn on hosts that serve the
// launcher from the root (e.g. knowledgebase-stage.adobe.com).
function getComponentOverride() {
    const params = new window.URLSearchParams(window.location.search);
    return {
        componentOverride: params.get('comp!entai-cdn'),
        hasOtherComponentOverride: [...params.keys()]
            .some(key => key.includes('comp!') && key !== 'comp!entai-cdn'),
    };
}

const { componentOverride, hasOtherComponentOverride } = getComponentOverride();

// __VERSION__ is replaced with the deploying version in the pipeline.
// as-dev-tools only resolves https:// plugin domains, so plain http (local dev)
// loads the current version folder.
if (/^\d+\.\d+\.\d+$/.test(componentOverride)) {
    loadModule(`${baseUrl}${componentOverride}`);
} else if (componentOverride !== null || hasOtherComponentOverride) {
    console.warn('[entai-launcher] Only same-origin version overrides are supported');
    loadModule(`${baseUrl}__VERSION__`);
} else if (pluginDomain.startsWith('https://')) {
    getPinnedVersionUri({ pluginDomain, latestVersion: '__VERSION__' })
        .then(loadModule)
        .catch(err => {
            console.error(`[entai-launcher] failed in getPinnedVersionUri, err=${err}`);
            loadModule(`${baseUrl}__VERSION__`);
        });
} else {
    loadModule(`${baseUrl}__VERSION__`);
}
